import { useSessionStorage } from '@vueuse/core'
import { LocalStorageKeys, LocalStorageKeyManager } from '~/store/LocalStorageKeyManager'
import type { Plugin } from 'vue'

type LastReload = {
  href: string
  at: number
}

const RELOAD_WINDOW_MS = 10_000

const CHUNK_LOAD_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Failed to load module script',
  "'text/html' is not a valid JavaScript MIME type",
]

const lastReload = useSessionStorage<LastReload>(
  LocalStorageKeyManager.createStorageKey(LocalStorageKeys.CHUNK_LOAD_RELOAD),
  { href: '', at: 0 },
  { flush: 'sync' },
)

let isLeavingPage = false

const hasReloadedRecently = (): boolean => {
  return (
    lastReload.value.href === window.location.href &&
    Date.now() - lastReload.value.at < RELOAD_WINDOW_MS
  )
}

const rememberReload = (): void => {
  lastReload.value = { href: window.location.href, at: Date.now() }
}

export const isChunkLoadError = (message: string): boolean => {
  return CHUNK_LOAD_ERROR_MESSAGES.some((chunkLoadErrorMessage) => {
    return message.includes(chunkLoadErrorMessage)
  })
}

/**
 * Firefox and Safari abort in-flight dynamic imports as soon as a navigation starts and reject
 * them, which Vite reports as a preload error. Reloading at that point cancels the navigation
 * (e.g. the redirect to Previo ID) and loops. Firefox fires `beforeunload` first, so the reload
 * is skipped once the page is leaving; iOS Safari fires no event before the rejection, so the
 * reload is additionally bounded to once per URL within a short window.
 */
export const recoverFromChunkLoadError = (): void => {
  if (isLeavingPage || hasReloadedRecently()) {
    return
  }

  rememberReload()
  window.location.reload()
}

/**
 * Reloads the app when a lazy chunk fails to load, typically because a deploy replaced the
 * hashed assets under a running tab.
 */
export const chunkLoadRecovery: Plugin = {
  install: (): void => {
    window.addEventListener('beforeunload', () => {
      isLeavingPage = true
    })

    window.addEventListener('vite:preloadError', () => {
      recoverFromChunkLoadError()
    })
  },
}
